import React from 'react';
import { PageHeader, SectionHeader } from '../components/SectionHeader';
import { statsExplanation } from '../data/nflData';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { BarChart3, TrendingUp, Cpu, Target, Zap, Activity } from 'lucide-react';

const Statistics = () => {
  return (
    <div className="min-h-screen" data-testid="statistics-page">
      <PageHeader 
        title="Estadísticas y Análisis"
        subtitle="Domina los números que definen el juego moderno"
        backgroundImage="https://images.unsplash.com/photo-1565789426992-9c335aa0a62c?auto=format&fit=crop&w=2000&q=80"
      />
      
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 space-y-24">
        
        {/* Intro */}
        <section>
          <SectionHeader 
            title="El Poder de los Datos"
            quarter="INTRO"
            icon={BarChart3}
          />
          
          <div className="tactical-card p-8 rounded-r-xl">
            <p className="text-lg text-slate-300 leading-relaxed">
              La NFL moderna es un deporte de <strong className="text-emerald-400">datos y análisis</strong>. 
              Desde estadísticas tradicionales hasta métricas avanzadas con sensores de última generación, 
              los números cuentan historias que el ojo no siempre puede ver. Aprender a leerlas te convertirá 
              en un aficionado más completo.
            </p>
          </div>
        </section>

        {/* Traditional Stats */}
        <section>
          <SectionHeader 
            title="Estadísticas Tradicionales"
            quarter="1Q"
            icon={TrendingUp}
          />
          
          <p className="text-slate-300 mb-8">
            Estas son las estadísticas que verás en cualquier retransmisión. Son el <strong className="text-white">lenguaje básico</strong> del 
            fútbol americano y esenciales para entender el rendimiento de jugadores y equipos.
          </p>
          
          <Tabs defaultValue="Quarterback" className="w-full">
            <TabsList className="flex flex-wrap bg-slate-800 rounded-xl p-1 mb-8 gap-1">
              {statsExplanation.traditional.map((category) => (
                <TabsTrigger 
                  key={category.category}
                  value={category.category}
                  className="data-[state=active]:bg-emerald-500 data-[state=active]:text-white rounded-lg font-bold text-xs md:text-sm px-3 py-2"
                  style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                >
                  {category.category}
                </TabsTrigger>
              ))}
            </TabsList>
            
            {statsExplanation.traditional.map((category) => (
              <TabsContent key={category.category} value={category.category}>
                <div className="grid md:grid-cols-2 gap-4">
                  {category.stats.map((stat, index) => (
                    <div key={index} className="info-card group">
                      <h4 
                        className="text-lg font-bold text-white mb-2 group-hover:text-emerald-400 transition-colors"
                        style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                      >
                        {stat.name}
                      </h4>
                      <p className="text-slate-400 text-sm">{stat.description}</p>
                    </div>
                  ))}
                </div>
              </TabsContent>
            ))}
          </Tabs>
        </section>

        {/* Advanced Stats */}
        <section>
          <SectionHeader 
            title="Estadísticas Avanzadas"
            quarter="2Q"
            icon={Cpu}
            subtitle="Next Gen Stats y analítica moderna"
          />
          
          <div className="bg-slate-800/50 p-6 rounded-xl border border-emerald-800/50 mb-8">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-emerald-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                <Activity className="w-6 h-6 text-emerald-400" />
              </div>
              <div>
                <h4 
                  className="text-xl font-bold text-white mb-2"
                  style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                >
                  Tecnología de Seguimiento
                </h4>
                <p className="text-slate-400 leading-relaxed">
                  La NFL usa <strong className="text-white">chips RFID</strong> en las hombreras de cada jugador y en el balón, 
                  registrando posiciones <strong className="text-emerald-400">muchas veces por segundo</strong>. 
                  Esto genera millones de datos por partido, permitiendo análisis que antes eran imposibles.
                </p>
              </div>
            </div>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6">
            {statsExplanation.advanced.map((stat, index) => (
              <div key={index} className="tactical-card p-6 rounded-r-xl group hover:border-l-emerald-400 transition-all duration-300">
                <div className="flex items-center gap-3 mb-3">
                  {index === 0 && <Target className="w-5 h-5 text-emerald-400" />}
                  {index === 1 && <TrendingUp className="w-5 h-5 text-blue-400" />}
                  {index === 2 && <Zap className="w-5 h-5 text-purple-400" />}
                  {index === 3 && <Activity className="w-5 h-5 text-orange-400" />}
                  {index >= 4 && <BarChart3 className="w-5 h-5 text-cyan-400" />}
                  <h4 
                    className="text-lg font-bold text-white uppercase"
                    style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                  >
                    {stat.name}
                  </h4>
                </div>
                <p className="text-slate-400 text-sm leading-relaxed">{stat.description}</p>
              </div>
            ))}
          </div>
        </section>

        {/* How Stats Help */}
        <section>
          <SectionHeader 
            title="¿Para Qué Sirven?"
            quarter="3Q"
            icon={Target}
          />
          
          <div className="space-y-6">
            <div className="grid md:grid-cols-3 gap-6">
              {[
                {
                  title: "Evaluar Jugadores",
                  description: "Ver más allá de los números básicos. Un QB puede tener pocas yardas pero alto EPA si sus pases son eficientes.",
                  icon: "👤",
                  color: "#3b82f6"
                },
                {
                  title: "Decisiones de Juego",
                  description: "¿Ir en 4th down? ¿Intentar field goal? Los datos ayudan a tomar decisiones óptimas basadas en probabilidades.",
                  icon: "📊",
                  color: "#22c55e"
                },
                {
                  title: "Análisis Post-Partido",
                  description: "Entender qué funcionó y qué no. Los equipos usan estas métricas para preparar el siguiente partido.",
                  icon: "📈",
                  color: "#f97316"
                }
              ].map((item, index) => (
                <div key={index} className="info-card">
                  <div 
                    className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 text-2xl"
                    style={{ backgroundColor: `${item.color}20` }}
                  >
                    {item.icon}
                  </div>
                  <h4 
                    className="text-lg font-bold text-white mb-2 uppercase"
                    style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                  >
                    {item.title}
                  </h4>
                  <p className="text-slate-400 text-sm">{item.description}</p>
                </div>
              ))}
            </div>
            
            <div className="tactical-card p-6 rounded-r-xl">
              <h4 
                className="text-xl font-bold text-white mb-4 uppercase"
                style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
              >
                Ejemplo Práctico: Passer Rating vs EPA
              </h4>
              <div className="grid md:grid-cols-2 gap-6 text-sm">
                <div>
                  <span className="text-emerald-400 font-bold block mb-2">Passer Rating (Tradicional)</span>
                  <p className="text-slate-400">
                    Un QB lanza 3 pases cortos de 3 yardas cada uno y los completa todos. 
                    Su passer rating será alto porque completó el 100%.
                  </p>
                </div>
                <div>
                  <span className="text-orange-400 font-bold block mb-2">EPA (Avanzada)</span>
                  <p className="text-slate-400">
                    Pero si necesitaba 10 yardas para el first down, su EPA será negativo 
                    porque esos pases no ayudaron a mover las cadenas.
                  </p>
                </div>
              </div>
              <p className="text-slate-300 mt-4 pt-4 border-t border-slate-700">
                Las estadísticas avanzadas dan <strong className="text-white">contexto</strong>. 
                No reemplazan a las tradicionales, las complementan.
              </p>
            </div>
          </div>
        </section>

        {/* Where to find */}
        <section>
          <div className="bg-slate-800/50 p-8 rounded-xl border border-slate-700">
            <h3 
              className="text-2xl font-bold text-white mb-4 uppercase"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              ¿Dónde ver estadísticas?
            </h3>
            <div className="grid md:grid-cols-2 gap-4 text-slate-400">
              <div>
                <strong className="text-white block mb-1">NFL.com/stats</strong>
                <p className="text-sm">Estadísticas oficiales tradicionales y avanzadas</p>
              </div>
              <div>
                <strong className="text-white block mb-1">Next Gen Stats</strong>
                <p className="text-sm">Métricas de tracking con visualizaciones</p>
              </div>
              <div>
                <strong className="text-white block mb-1">Pro Football Reference</strong>
                <p className="text-sm">Base de datos histórica completa</p>
              </div>
              <div>
                <strong className="text-white block mb-1">ESPN/Yahoo Sports</strong>
                <p className="text-sm">Estadísticas en tiempo real durante partidos</p>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Statistics;
